import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"

export default class extends Controller {
    static values = {
        show: Boolean,
        url: String,
        firstName: String,
        lastName: String,
        email: String,
        phone: String
    }

    connect() {
        if (!this.showValue) return
        setTimeout(() => this.openPrompt(), 500)
    }

    async openPrompt() {
        const result = await Swal.fire({
            title: "Help us keep your contact<br>details up-to-date",
            html: `
                <div class="text-left space-y-4 mt-4">
                  <p class="text-sm text-p">
                    To keep your Donor Hub accurate, please review and confirm your primary contact details.
                  </p>

                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label class="block text-xs font-bold text-slate-500 mb-1.5">First name</label>
                      <input
                        id="swal-primary-first-name"
                        class="swal2-input !m-0 !w-full !h-[44px] !px-4 !text-[15px] !rounded-xl !border !border-slate-200 !shadow-none focus:!border-primary focus:!ring-2 focus:!ring-primary/20"
                        value="${this.escape(this.firstNameValue)}"
                      >
                    </div>
                    <div>
                      <label class="block text-xs font-bold text-slate-500 mb-1.5">Last name</label>
                      <input
                        id="swal-primary-last-name"
                        class="swal2-input !m-0 !w-full !h-[44px] !px-4 !text-[15px] !rounded-xl !border !border-slate-200 !shadow-none focus:!border-primary focus:!ring-2 focus:!ring-primary/20"
                        value="${this.escape(this.lastNameValue)}"
                      >
                    </div>
                  </div>

                  <div>
                    <label class="block text-xs font-bold text-slate-500 mb-1.5">Email</label>
                    <input
                      id="swal-primary-email"
                      type="email"
                      class="swal2-input !m-0 !w-full !h-[44px] !px-4 !text-[15px] !rounded-xl !border !border-slate-200 !shadow-none focus:!border-primary focus:!ring-2 focus:!ring-primary/20"
                      value="${this.escape(this.emailValue)}"
                    >
                  </div>

                  <div>
                    <label class="block text-xs font-bold text-slate-500 mb-1.5">Phone number</label>
                    <input
                      id="swal-primary-phone"
                      class="swal2-input !m-0 !w-full !h-[44px] !px-4 !text-[15px] !rounded-xl !border !border-slate-200 !shadow-none focus:!border-primary focus:!ring-2 focus:!ring-primary/20"
                      value="${this.escape(this.phoneValue)}"
                    >
                  </div>

                  <label class="flex items-start gap-3 mt-5 cursor-pointer select-none">
                    <input
                      id="swal-primary-confirm"
                      type="checkbox"
                      class="peer sr-only"
                    >
                    <span class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border border-slate-300 bg-white transition peer-checked:border-primary peer-checked:bg-primary">
                      <svg class="h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100 transition" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3.2-3.2a1 1 0 111.414-1.42l2.493 2.494 6.493-6.494a1 1 0 011.414 0z" clip-rule="evenodd"/>
                      </svg>
                    </span>
                    <span class="p-text text-sm leading-relaxed">
                      All information above is up to date.
                    </span>
                  </label>
                </div>
            `,
            width: 640,
            confirmButtonText: "Save and continue",
            showCancelButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
                popup: "rounded-2xl",
                confirmButton: "swal2-confirm bg-primary text-white font-bold px-6 py-3 rounded-xl"
            },
            preConfirm: async () => {
                const firstName = document.getElementById("swal-primary-first-name")?.value.trim()
                const lastName  = document.getElementById("swal-primary-last-name")?.value.trim()
                const email     = document.getElementById("swal-primary-email")?.value.trim()
                const phone     = document.getElementById("swal-primary-phone")?.value.trim()
                const confirmed = document.getElementById("swal-primary-confirm")?.checked

                if (!firstName || !lastName || !email || !phone) {
                    Swal.showValidationMessage("Please complete all primary contact fields.")
                    return false
                }

                if (!confirmed) {
                    Swal.showValidationMessage("Please confirm that the information is up to date.")
                    return false
                }

                try {
                    const response = await fetch(this.urlValue, {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
                            "Accept": "application/json"
                        },
                        body: JSON.stringify({
                            primary_first_name: firstName,
                            primary_last_name: lastName,
                            primary_email: email,
                            primary_phone_number: phone
                        })
                    })

                    const data = await response.json()

                    if (!response.ok || !data.success) {
                        throw new Error(data.error || "Something went wrong.")
                    }

                    return true
                } catch (error) {
                    Swal.showValidationMessage(error.message)
                    return false
                }
            }
        })

        if (result.isConfirmed) {
            let timerInterval

            await Swal.fire({
                icon: "success",
                title: "All set! 🎉",
                html: `
            Your primary contact details have been confirmed.
            <br><br>
            <span class="text-sm text-slate-400">
              Closing in <b>4</b> seconds...
            </span>
        `,
                timer: 4000,
                timerProgressBar: true,
                showConfirmButton: false,
                allowOutsideClick: false,
                showClass: {
                    popup: "animate__animated animate__fadeInUp animate__faster"
                },
                hideClass: {
                    popup: "animate__animated animate__fadeOut animate__faster"
                },
                customClass: {
                    popup: "rounded-2xl"
                },
                didOpen: () => {
                    const b = Swal.getHtmlContainer().querySelector("b")
                    timerInterval = setInterval(() => {
                        b.textContent = Math.ceil(Swal.getTimerLeft() / 1000)
                    }, 100)
                },
                willClose: () => {
                    clearInterval(timerInterval)
                }
            })
        }
    }

    escape(value) {
        return (value || "")
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
    }
}